import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  Bell,
  User,
  Menu,
  Users,
  ChevronDown,
  Package,
  ShoppingCart,
  BarChart3,
  FileText,
  Settings,
  Heart,
  HelpCircle,
  Store,
  LayoutDashboard,
  LogOut,
  CreditCard,
  Globe,
  MessageCircle,
  ClipboardList,
  Mail,
} from "lucide-react";
import {
  Button,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
} from "@b2b/ui-components";
import { navigationConfigs, NavigationItem } from "@b2b/ui-components";
import { BizcoverIndiaBrandMark } from "../Branding/BizcoverIndiaBrandMark";
import {
  HeaderChatMessagesSlot,
  type HeaderChatUnread,
} from "./HeaderChatMessages";
import {
  HeaderNotificationsSlot,
  type HeaderNotifications,
} from "./HeaderNotifications";
import { resolveHeaderAvatarUrl } from "./buyerHeaderAvatar";
import { FiMenu } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
// Centralized icon map
const iconMap = {
  Users,
  Package,
  ShoppingCart,
  BarChart3,
  FileText,
  Settings,
  Heart,
  HelpCircle,
  Store,
  LayoutDashboard,
  CreditCard,
  User,
  MessageCircle,
  Mail,
};

interface HeaderProps {
  role: "admin" | "buyer" | "seller" | "both";
  userRole?: "admin" | "seller" | "buyer";
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
  onLogout?: () => void; // Optional logout handler from AuthProvider
  onCategoriesClick?: () => void; // Handler for Categories button click
  /** When provided (seller), profile menu is the same on every screen, driven by sidebar logic. */
  sellerUserMenuItems?: NavigationItem[];
  searchBar?: React.ReactNode;
  chatUnread?: HeaderChatUnread;
  notifications?: HeaderNotifications;
  /** @deprecated Prefer `notifications.unreadCount` */
  notificationUnreadCount?: number;
}

const UpdatedHeader: React.FC<HeaderProps> = ({
  role,
  userRole: userRoleProp,
  onToggleSidebar,
  isSidebarOpen,
  onLogout,
  onCategoriesClick,
  searchBar,
  sellerUserMenuItems,
  chatUnread,
  notifications,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = navigationConfigs[role];

  const [hasToken, setHasToken] = useState<boolean>(false);
  const [emailPasswordUser, setEmailPasswordUser] = useState<{
    name?: string;
    email?: string;
    role?: string;
  } | null>(null);

  const userRoleFromAuth = undefined; // We'll rely on props or API fetch for role
  const [actualUserRole, setActualUserRole] = useState<
    "admin" | "seller" | "buyer" | undefined
  >(userRoleProp || userRoleFromAuth);

  const [buyerAvatarUrl, setBuyerAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const checkToken = () => {
      setHasToken(!!localStorage.getItem("bt_b2b_access_token"));
    };
    checkToken();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "bt_b2b_access_token") checkToken();
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!hasToken) {
      setBuyerAvatarUrl(null);
      return;
    }

    let cancelled = false;

    const fetchUserData = async (previewProfileImageUrl?: string) => {
      try {
        const { authService } = await import("@b2b/services");
        const meResponse = await authService.getMe();
        if (cancelled) return;

        if (meResponse.status === "active" && meResponse.user) {
          const user = meResponse.user;
          const userName =
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.firstName || user.email || "";

          setEmailPasswordUser({
            name: userName,
            email: user.email,
            role: user.role,
          });

          // Also update actualUserRole if not set - use functional update to avoid dependency
          setActualUserRole((currentRole) => {
            if (!currentRole && user.role) {
              const roleFromApi = user.role.toLowerCase();
              if (
                roleFromApi === "super_admin" ||
                roleFromApi === "company_admin"
              ) {
                return "admin";
              } else if (
                roleFromApi === "seller" ||
                roleFromApi === "buyer_seller"
              ) {
                return "seller";
              } else if (roleFromApi === "buyer") {
                return "buyer";
              }
            }
            return currentRole;
          });

          const resolvedAvatarUrl = await resolveHeaderAvatarUrl(
            role,
            user,
            previewProfileImageUrl,
          );
          if (!cancelled) setBuyerAvatarUrl(resolvedAvatarUrl);
        } else {
          setBuyerAvatarUrl(null);
        }
      } catch (error) {
        console.error("Error fetching user data in Header:", error);
        if (!cancelled) setBuyerAvatarUrl(null);
      }
    };

    void fetchUserData();

    const onProfileUpdated = (e: Event) => {
      const detail = (e as CustomEvent<{ profileImageUrl?: string }>).detail;
      void fetchUserData(detail?.profileImageUrl);
    };
    window.addEventListener("bt_b2b_profile_updated", onProfileUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener("bt_b2b_profile_updated", onProfileUpdated);
    };
  }, [hasToken, role]);

  const isAuthenticated = hasToken;

  const pathname = location.pathname;

  // Buyer/seller app pages that use the same simplified header chrome as dashboard
  const isDashboardPage =
    pathname === "/admin/dashboard" ||
    pathname === "/buyer/dashboard" ||
    pathname === "/buyer/analytics" ||
    pathname === "/buyer/favorites" ||
    pathname === "/buyer/enquiries" ||
    pathname === "/buyer/profile_settings" ||
    pathname.startsWith("/buyer/profile_settings/") ||
    pathname === "/seller/dashboard" ||
    pathname === "/seller/analytics";

  // Check if we're on the home page
  const isHomePage = pathname === "/home" || pathname === "/";

  // Check if we're on routes where Categories, All products, Sellers, and Pricing should be hidden
  // Match exact path or path with query parameters or sub-routes
  const isCategoriesPage =
    pathname === "/categories" || pathname.startsWith("/categories/");
  const isSearchPage =
    pathname === "/search" ||
    (pathname.startsWith("/search/") && pathname.length > 7);
  const isSellersPage =
    pathname === "/sellers" || pathname.startsWith("/sellers/");
  const isPricingPage =
    pathname === "/pricing" ||
    pathname === "/membership-plans" ||
    pathname.startsWith("/pricing/");
  const isProductDetailPage =
    pathname.startsWith("/product/") ||
    pathname.startsWith("/bizcover/product/");
  const isChatPage = pathname === "/chat" || pathname.startsWith("/chat");
  const shouldHideNavItems =
    isCategoriesPage ||
    isSearchPage ||
    isSellersPage ||
    isPricingPage ||
    isProductDetailPage ||
    isChatPage;

  // Update user role when it changes from AuthProvider or props
  useEffect(() => {
    if (userRoleProp) {
      setActualUserRole(userRoleProp);
    } else if (userRoleFromAuth) {
      setActualUserRole(userRoleFromAuth);
    }
  }, [userRoleProp, userRoleFromAuth]);

  // For Auth0 users, completely rely on AuthProvider to fetch role
  // DO NOT make redundant API calls here - AuthProvider already handles this
  // If role is not available, it means AuthProvider is still loading or user is not authenticated
  // Just use the role from props (userRoleProp) which comes from AuthProvider via AppLayout

  if (!config) return null;

  // Filter items based on dashboard page
  // Show Categories button on home page (don't filter it out)
  let primaryNavItems = isDashboardPage ? [] : config.primaryNavItems;

  // Hide marketplace nav on seller app routes (/seller/...), not on /seller_detail/... (public storefront).
  const isSellerAppPage =
    location.pathname.startsWith("/seller") &&
    !location.pathname.startsWith("/seller_detail");

  // Only hide these buttons on seller app pages or on specific content pages (pricing, search, product detail)
  if (isSellerAppPage || shouldHideNavItems) {
    primaryNavItems = primaryNavItems.filter(
      (item) =>
        item.label !== "Categories" &&
        item.label !== "All products" &&
        item.label !== "Sellers" &&
        item.label !== "Pricing",
    );
  }

  // On chat page, also hide Products and RFQs for sellers
  if (isChatPage && (role === "seller" || actualUserRole === "seller")) {
    primaryNavItems = primaryNavItems.filter(
      (item) => item.label !== "Products" && item.label !== "RFQs",
    );
  }
  let userMenuItems: NavigationItem[];
  if (sellerUserMenuItems != null) {
    // Seller: same profile menu on every screen (driven by sidebar completion logic)
    userMenuItems = sellerUserMenuItems;
  } else {
    // Keep full menu on dashboard routes (buyer/admin); nav chrome is simplified elsewhere.
    userMenuItems = config.userMenuItems;
  }

  // Remove Messages from menu for admin users on home page
  if (isHomePage && actualUserRole === "admin") {
    userMenuItems = userMenuItems.filter((item) => item.label !== "Messages");
  }
  const showRightButtons = !isDashboardPage && config.rightButtons;
  const showSearch = !isDashboardPage && primaryNavItems.length > 0;

  // Get user name and email - use real user data; avoid showing placeholder (e.g. "John Buyer") while loading
  const userName =
    emailPasswordUser?.name ??
    (hasToken && emailPasswordUser === null ? "Account" : config.userInfo.name);
  const userEmail =
    emailPasswordUser?.email ??
    (hasToken && emailPasswordUser === null ? "" : config.userInfo.subtitle);

  // Determine dashboard route based on actual user role (from props, AuthProvider, or API)
  const getDashboardRoute = async () => {
    // Priority 1: Use actualUserRole if available (from props or AuthProvider)
    if (actualUserRole) {
      if (actualUserRole === "admin") return "/admin/dashboard";
      if (actualUserRole === "seller") return "/seller/dashboard";
      if (actualUserRole === "buyer") return "/buyer/dashboard";
    }

    // Priority 2: Use role prop if available and not "both"
    if (role !== "both") {
      if (role === "admin") return "/admin/dashboard";
      if (role === "seller") return "/seller/dashboard";
      if (role === "buyer") return "/buyer/dashboard";
    }

    // Priority 3: If we have a token, fetch role from API
    if (hasToken) {
      try {
        const { authService } = await import("@b2b/services");
        const meResponse = await authService.getMe();

        if (meResponse.status === "active" && meResponse.user?.role) {
          const roleFromApi = meResponse.user.role.toLowerCase();

          if (
            roleFromApi === "super_admin" ||
            roleFromApi === "company_admin"
          ) {
            return "/admin/dashboard";
          } else if (
            roleFromApi === "seller" ||
            roleFromApi === "buyer_seller"
          ) {
            return "/seller/dashboard";
          } else if (roleFromApi === "buyer") {
            return "/buyer/dashboard";
          }
        }
      } catch (error) {
        console.error(
          "Error fetching user role for dashboard navigation:",
          error,
        );
      }
    }

    // Default fallback
    return "/buyer/dashboard";
  };

  // Determine profile route based on actual user role (from props, AuthProvider, or API)
  const getProfileRoute = async () => {
    // Priority 1: Use actualUserRole if available (from props or AuthProvider)
    if (actualUserRole) {
      if (actualUserRole === "admin") return "/admin/profile_settings";
      if (actualUserRole === "seller") return "/seller/profile";
      if (actualUserRole === "buyer") return "/buyer/profile";
    }

    // Priority 2: Use role prop if available and not "both"
    if (role !== "both") {
      if (role === "admin") return "/admin/profile_settings";
      if (role === "seller") return "/seller/profile";
      if (role === "buyer") return "/buyer/profile";
    }

    // Priority 3: If we have a token, fetch role from API
    if (hasToken) {
      try {
        const { authService } = await import("@b2b/services");
        const meResponse = await authService.getMe();

        if (meResponse.status === "active" && meResponse.user?.role) {
          const roleFromApi = meResponse.user.role.toLowerCase();

          if (
            roleFromApi === "super_admin" ||
            roleFromApi === "company_admin"
          ) {
            return "/admin/profile_settings";
          } else if (
            roleFromApi === "seller" ||
            roleFromApi === "buyer_seller"
          ) {
            return "/seller/profile";
          } else if (roleFromApi === "buyer") {
            return "/buyer/profile";
          }
        }
      } catch (error) {
        console.error(
          "Error fetching user role for profile navigation:",
          error,
        );
      }
    }

    // Default fallback
    return "/buyer/profile";
  };

  console.log("🚀 ~ Header ~ actualUserRole:", actualUserRole)

  // Handle logout - use provided logout handler or clear token and redirect
  const handleLogout =
    onLogout ||
    (() => {
      try {
        localStorage.removeItem("bt_b2b_access_token");
        window.location.href = "/login";
      } catch {
        window.location.href = "/login";
      }
    });

  // Helpers
  const handleNavigate = (href: string) => {
    if (href && href !== "/logout") {
      // If navigating to dashboard, get the correct route based on role
      if (href.includes("dashboard") || href === "/") {
        getDashboardRoute().then((route) => {
          navigate(route);
        });
      } else {
        navigate(href);
      }
    }
  };
  const renderIcon = (name?: string) => {
    const Icon = name ? iconMap[name as keyof typeof iconMap] : null;
    return Icon ? <Icon className="h-4 w-4 mr-2 text-black" /> : null;
  };

  /** Match buyer profile dropdown: envelope icon for Messages (same as `icon: "Mail"`). */
  const renderUserMenuItemIcon = (item: NavigationItem) => {
    if (item.label === "Messages") {
      return <Mail className="h-4 w-4 mr-2 text-black" />;
    }
    return renderIcon(item.icon);
  };

  // Render nav (top links) - Adapted for UpdatedHeader styling
  const NavItem = ({ item }: { item: NavigationItem }) => {
    const Icon = item.icon ? iconMap[item.icon as keyof typeof iconMap] : null;

    // Items that should open in a new tab
    const openInNewTabItems = ["All products", "Sellers", "Pricing"];
    const shouldOpenInNewTab = openInNewTabItems.includes(item.label);

    // Handle Categories button click on homepage
    const isCategoriesOnHomePage = item.label === "Categories" && isHomePage;

    const handleItemClick = (href: string) => {
      // If Categories button on homepage, trigger mega menu
      if (isCategoriesOnHomePage && onCategoriesClick) {
        onCategoriesClick();
        return;
      }

      if (shouldOpenInNewTab && href) {
        // Construct full URL for relative paths
        const fullUrl = href.startsWith("http")
          ? href
          : `${window.location.origin}${href}`;
        window.open(fullUrl, "_blank", "noopener,noreferrer");
      } else {
        handleNavigate(href);
      }
    };

    // If Categories on homepage, render as clickable button to trigger mega menu
    if (isCategoriesOnHomePage && onCategoriesClick) {
      return (
        <div
          className="flex items-center gap-2 hover:text-[#F97316] cursor-pointer transition-colors text-sm text-gray-700 font-bold"
          onClick={() => onCategoriesClick()}
        >
          {Icon && <Icon className="h-5 w-5" />}
          <span>{item.label}</span>
          {item.children?.length && item.children?.length > 0 && (
            <ChevronDown className="h-4 w-4 ml-1" />
          )}
        </div>
      );
    }

    return item.children?.length ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center gap-2 hover:text-[#F97316] cursor-pointer transition-colors text-sm text-gray-700 font-bold">
            {Icon && <Icon className="h-5 w-5" />}
            <span>{item.label}</span>
            <ChevronDown className="h-4 w-4 ml-1" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="p-0">
          {item.children.map((c) => {
            const ChildIcon = c.icon
              ? iconMap[c.icon as keyof typeof iconMap]
              : null;
            return (
              <DropdownMenuItem
                key={c.label}
                onClick={() => handleNavigate(c.href || "")}
                className="text-black focus:bg-orange-100 bg-white"
              >
                {ChildIcon && <ChildIcon className="h-4 w-4 mr-2 text-black" />}
                {c.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <div
        className="flex items-center gap-2 hover:text-[#F97316] cursor-pointer transition-colors text-sm text-gray-700 font-bold"
        onClick={() => handleItemClick(item.href || "")}
      >
        {Icon && <Icon className="h-5 w-5" />}
        <span>{item.label}</span>
      </div>
    );
  };

  // Render user menu
  const UserMenuItem = ({ item }: { item: NavigationItem }) => {
    if (item.label === "Logout") {
      return (
        <DropdownMenuItem
          key={item.label}
          onClick={handleLogout}
          className="text-black focus:bg-orange-100"
        >
          <LogOut className="h-4 w-4 mr-2 text-black" /> {item.label}
        </DropdownMenuItem>
      );
    }
    // Handle Profile / Business Profile navigation
    if (item.label === "Profile" || item.label === "Business Profile") {
      return (
        <DropdownMenuItem
          key={item.label}
          onClick={() => {
            const href = item.href;
            if (href) {
              navigate(href);
            } else {
              getProfileRoute().then((route) => navigate(route));
            }
          }}
          className="text-black focus:bg-orange-100"
        >
          {renderUserMenuItemIcon(item)} {item.label}
        </DropdownMenuItem>
      );
    }
    return (
      <DropdownMenuItem
        key={item.label}
        onClick={() => handleNavigate(item.href || "")}
        className="text-black focus:bg-orange-100"
      >
        {renderUserMenuItemIcon(item)} {item.label}
      </DropdownMenuItem>
    );
  };

  return (
    // <div className="bg-white border-b border-gray-200 shadow-sm">
    //   <header className="h-16 px-4 sm:px-6 flex items-center justify-between">
    //     {/* Left: Menu + Logo + Nav */}
    //     <div className="flex items-center gap-6">
    //       <Button
    //         variant="ghost"
    //         size="sm"
    //         onClick={onToggleSidebar}
    //         className="lg:hidden hover:bg-orange-100 text-black"
    //       >
    //         {isSidebarOpen ? <IoMdClose size={24} /> : <FiMenu size={24} />}
    //       </Button>

    //       {/* Logo */}
    //       <div
    //         className="flex items-center gap-2 cursor-pointer"
    //         onClick={() => handleNavigate("/home")}
    //       >
    //         <div className="w-9 h-9 bg-orange-500 rounded flex items-center justify-center">
    //           <span className="text-white font-bold text-lg">
    //             {config.logo.letter}
    //           </span>
    //         </div>
    //         <span className="font-bold text-xl text-black hidden sm:block">
    //           {config.logo.text}
    //         </span>
    //       </div>

    //       {/* Nav links */}
    //       <nav className="hidden lg:flex items-center gap-1">
    //         {primaryNavItems.map((i) => (
    //           <NavItem key={i.label} item={i} />
    //         ))}
    //       </nav>
    //     </div>

    //     {/* Center: Search - Hidden */}
    //     {/* {showSearch && (
    //       <div className="flex-1 max-w-2xl mx-6 hidden md:block">
    //         <div className="relative">
    //           <Input
    //             placeholder={config.searchPlaceholder}
    //             className="h-10 pl-4 pr-20 rounded-full border-gray-300 focus:border-orange-500 focus:ring-orange-500 text-black"
    //           />
    //           <Button className="absolute right-1 top-1 h-8 px-4 rounded-full bg-orange-500 hover:bg-orange-600 text-white">
    //             <Search className="h-4 w-4 mr-1" /> Search
    //           </Button>
    //         </div>
    //       </div>
    //     )} */}

    //     {/* Right: Buttons + User */}
    //     <div className="flex items-center gap-2 sm:gap-3">
    //       {/* Mobile search - Hidden */}
    //       {/* {showSearch && (
    //         <Button
    //           variant="ghost"
    //           size="sm"
    //           className="md:hidden hover:bg-orange-100 text-black"
    //         >
    //           <Search className="h-4 w-4" />
    //         </Button>
    //       )} */}

    //       {/* Conditional buttons */}
    //       {showRightButtons &&
    //         typeof showRightButtons === "object" &&
    //         showRightButtons.cart && (
    //           <Button
    //             variant="ghost"
    //             size="sm"
    //             className="relative hover:bg-orange-100 text-black"
    //           >
    //             <ShoppingCart className="h-4 w-4 mr-1" />{" "}
    //             <span className="hidden lg:inline">Cart</span>
    //             {config.badges?.cart && (
    //               <Badge className="absolute -top-2 -right-2 h-5 w-5 p-1 text-xs bg-red-600 text-white">
    //                 {config.badges.cart}
    //               </Badge>
    //             )}
    //           </Button>
    //         )}
    //       {showRightButtons &&
    //         typeof showRightButtons === "object" &&
    //         showRightButtons.notifications && (
    //           <Button
    //             variant="ghost"
    //             size="sm"
    //             className="relative hover:bg-orange-100 text-black"
    //           >
    //             <Bell className="h-4 w-4" />
    //             {config.badges?.notifications && (
    //               <Badge className="absolute -top-2 -right-2 h-5 w-5 p-1 text-xs bg-red-600 text-white">
    //                 {config.badges.notifications}
    //               </Badge>
    //             )}
    //           </Button>
    //         )}

    //       {!isAuthenticated && (
    //         <Button
    //           className="h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
    //           onClick={() => navigate("/login")}
    //         >
    //           Sign In
    //         </Button>
    //       )}

    //       {/* User dropdown */}
    //       {isAuthenticated && (
    //         <DropdownMenu>
    //           <DropdownMenuTrigger asChild>
    //             <Button
    //               variant="ghost"
    //               className="flex items-center hover:bg-orange-100 text-black"
    //             >
    //               <Avatar className="h-7 w-7 mr-2">
    //                 <AvatarImage src="/placeholder.svg" />
    //                 <AvatarFallback className="bg-gray-200 text-black text-xs">
    //                   {userName?.charAt(0)?.toUpperCase() || (
    //                     <User className="h-3 w-3" />
    //                   )}
    //                 </AvatarFallback>
    //               </Avatar>
    //               <span className="hidden sm:inline">
    //                 {userName ||
    //                   (role === "admin"
    //                     ? "Admin"
    //                     : role === "buyer"
    //                       ? "Account"
    //                       : "Seller")}
    //               </span>
    //               <ChevronDown className="h-3 w-3 ml-1" />
    //             </Button>
    //           </DropdownMenuTrigger>
    //           <DropdownMenuContent align="end" className="w-56 bg-white">
    //             <DropdownMenuLabel>
    //               <div className="flex flex-col">
    //                 <p className="text-sm font-medium text-black">{userName}</p>
    //                 <p className="text-xs text-gray-500">{userEmail}</p>
    //               </div>
    //             </DropdownMenuLabel>
    //             <DropdownMenuSeparator />
    //             {/* My Dashboard menu item */}
    //             {isAuthenticated && !isDashboardPage && (
    //               <>
    //                 <DropdownMenuItem
    //                   onClick={() => {
    //                     getDashboardRoute().then((route) => {
    //                       navigate(route);
    //                     });
    //                   }}
    //                   className="text-black focus:bg-orange-100"
    //                 >
    //                   <LayoutDashboard className="h-4 w-4 mr-2 text-black" /> My
    //                   Dashboard
    //                 </DropdownMenuItem>
    //                 <DropdownMenuSeparator />
    //               </>
    //             )}
    //             {userMenuItems.map((item) =>
    //               item.label === "Logout" ? (
    //                 <React.Fragment key={item.label}>
    //                   <DropdownMenuSeparator />
    //                   <UserMenuItem item={item} />
    //                 </React.Fragment>
    //               ) : item.label === "My Dashboard" ? null : (
    //                 <UserMenuItem key={item.label} item={item} />
    //               )
    //             )}
    //           </DropdownMenuContent>
    //         </DropdownMenu>
    //       )}
    //     </div>
    //   </header>
    // </div>
    <nav className="sticky top-0 z-50 shrink-0 bg-white border-b border-gray-200">
      {/* Top Row - Logo and Utility Links */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between py-2.5">
          {/* Left - Logo */}
          {/* <div className="flex items-center gap-1">
            <div className="text-[#F97316] text-2xl font-bold leading-none">
              e
            </div>
            <span className="text-[#F97316] text-lg font-semibold leading-tight">
              bizcoverindia.com
            </span>
          </div> */}

          {/* Left: Menu + Logo + Nav */}
          <div className="flex items-center">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleSidebar}
              className="lg:hidden hover:bg-orange-100 text-black"
            >
              {isSidebarOpen ? <IoMdClose size={24} /> : <FiMenu size={24} />}
            </Button>

            {/* Logo */}
            <BizcoverIndiaBrandMark variant="header" to="/home" />
          </div>
          {/* {isHomePage && (
            <div className="gap-2 text-sm text-gray-700">
              {primaryNavItems.filter((item) => item.label === "Categories").map((item) => (
                <NavItem key={item.label} item={item} />
              ))}
            </div>
          )} */}
          {searchBar && (
            <div className="flex-1 max-w-xl hidden md:block">
              {searchBar}
            </div>
          )}
          {/* show catgory nav items only on home page */}
          
          {/* Right - Utility Links */}
          <div className="flex items-center  text-sm text-gray-700">
            {/* <div className="flex items-center gap-1.5 hover:text-[#F97316] cursor-pointer transition-colors">
              <span className="text-sm">Deliver to:</span>
              <span className="text-base leading-none">🇮🇳</span>
              <span className="text-sm">IN</span>
            </div> */}
            {/* <div className="flex items-center gap-1.5 hover:text-[#F97316] cursor-pointer transition-colors">
              <Globe className="h-4 w-4" />
              <span className="text-sm">English-USD</span>
            </div> */}
            {/* Messages: show on hub/simplified header too; not gated by showRightButtons. Hidden on /chat. */}
            {!isChatPage &&
            actualUserRole !== "admin" &&
            Boolean(config.rightButtons?.messages) ? (
              <HeaderChatMessagesSlot
                chatUnread={chatUnread}
                showMessagesButton
                isAuthenticated={isAuthenticated}
                variant="comfortable"
              />
            ) : null}
            {isAuthenticated && Boolean(config.rightButtons?.notifications) ? (
                <HeaderNotificationsSlot
                  notifications={notifications}
                  showNotificationsButton
                  isAuthenticated={isAuthenticated}
                  variant="comfortable"
                />
              ) : null}

            {!isAuthenticated && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="h-8 rounded-full border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700"
                  onClick={() => navigate("/registration")}
                >
                  Create Account
                </Button>
                <Button
                  className="h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>
              </div>
            )}

            {/* User dropdown */}
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center hover:bg-orange-100 text-black outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  >
                    <Avatar className="h-7 w-7 mr-2">
                      <AvatarImage
                        src={buyerAvatarUrl || "/placeholder.svg"}
                      />
                      <AvatarFallback className="bg-gray-200 text-black text-xs">
                        {userName?.charAt(0)?.toUpperCase() || (
                          <User className="h-3 w-3" />
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline">
                      {userName ||
                        (role === "admin"
                          ? "Admin"
                          : role === "buyer"
                            ? "Account"
                            : "Seller")}
                    </span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-black">
                        {userName}
                      </p>
                      <p className="text-xs text-gray-500">{userEmail}</p>
                    </div>
                  </DropdownMenuLabel>
                  {/* <DropdownMenuSeparator /> */}
                  {/* My Dashboard menu item */}
                  {isAuthenticated && (
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          getDashboardRoute().then((route) => {
                            navigate(route);
                          });
                        }}
                        className="text-black focus:bg-orange-100"
                      >
                        <LayoutDashboard className="h-4 w-4 mr-2 text-black" />{" "}
                        My Dashboard
                      </DropdownMenuItem>
                      {/* <DropdownMenuSeparator /> */}
                    </>
                  )}
                  {userMenuItems.map((item) =>
                    item.label === "Logout" ? (
                      <React.Fragment key={item.label}>
                        {/* <DropdownMenuSeparator /> */}
                        <UserMenuItem item={item} />
                      </React.Fragment>
                    ) : item.label === "My Dashboard" ? null : (
                      <UserMenuItem key={item.label} item={item} />
                    ),
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row - Navigation Links */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-2.5">
            {/* Left - Category Links */}
            <div className="flex items-center gap-6 text-sm text-gray-700">
              {primaryNavItems.map((item) => (
                <NavItem key={item.label} item={item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export { UpdatedHeader };
